import { type TimeRange } from 'src/shared/components/span-timeline/timeAxis';
import type { StacItem } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';
import { IN_FORCE_UNTIL } from '../documents/stacItemDocument';

export type { TimeRange };

/**
 * How long each version was the one in force.
 *
 * Validity is derived, never stored: a version holds from the instant it was computed until
 * the next one was (ADR-016 decision 2). So the bar, not the point, is the honest mark — a
 * version is a span of time during which it was what the engine served.
 */
export type ValiditySegment = {
    version: StacItem;
    from: Date;
    /** `null` while still in force, which is what draws it to the edge of the window. */
    to: Date | null;
    /** Computed before the window opened, and carried in so the window is not blank. */
    carriedIn: boolean;
    /**
     * Something newer replaced it at an instant outside the window, so `to` is where the
     * drawing stops rather than where the version did. Nothing here knows the real end, and
     * reporting the window's edge as a date would say it ended then.
     */
    endsBeyondWindow: boolean;
};

export type ValiditySegmentsArgs = {
    /** The versions computed inside the window, in whatever order the engine answered. */
    versions: StacItem[];
    /** The version already in force when the window opened, if the window does not hold it. */
    carriedIn?: StacItem;
    range: TimeRange;
    /** The version the engine serves now. Only this one is genuinely open ended. */
    inForceId?: string;
};

const computedAt = (version: StacItem): Date | null => (version.properties.datetime ? new Date(version.properties.datetime) : null);

/**
 * When a version was closed, if it was.
 *
 * Closing is how a version stops being in force with *nothing* replacing it — under ordering
 * alone the newest would be current for ever (ADR-016, rollback).
 */
const closedAt = (version: StacItem): Date | null => {
    const declared = version.properties[IN_FORCE_UNTIL];
    return typeof declared === 'string' ? new Date(declared) : null;
};

export const validitySegmentsOf = ({ versions, carriedIn, range, inForceId }: ValiditySegmentsArgs): ValiditySegment[] => {
    const held = [...versions];
    if (carriedIn && !held.some(version => version.id === carriedIn.id)) {
        held.push(carriedIn);
    }

    const ordered = held
        // A version without an instant could never be resolved, so it cannot be placed either.
        .filter(version => computedAt(version) !== null)
        .sort((a, b) => {
            const byInstant = computedAt(a)!.getTime() - computedAt(b)!.getTime();
            // The engine breaks a tie on when we catalogued it: of two runs claiming one
            // moment, the one we learned of second is the correction.
            return byInstant !== 0 ? byInstant : (a.properties.created ?? '').localeCompare(b.properties.created ?? '');
        });

    return ordered.map((version, index) => {
        const next = ordered[index + 1];
        const start = computedAt(version)!;
        // Open ended only when the engine says this is what it serves. A later version may
        // exist beyond the window, and then the last one drawn is not ongoing.
        const closure = closedAt(version);
        const replacedAt = next ? computedAt(next)! : null;
        // Whichever came first ends it: a closure cannot extend a version past its successor,
        // and a successor cannot revive one already closed.
        const ends = replacedAt && closure ? new Date(Math.min(replacedAt.getTime(), closure.getTime())) : replacedAt ?? closure;

        const isStillInForce = !ends && version.id === inForceId;
        const endsBeyondWindow = !ends && !isStillInForce;

        return {
            version,
            // A version carried in began before the window; it draws from the edge.
            from: start.getTime() < range.from.getTime() ? range.from : start,
            to: ends ?? (isStillInForce ? null : range.to),
            carriedIn: start.getTime() < range.from.getTime(),
            endsBeyondWindow,
        };
    });
};

/**
 * Where the marker must sit for a version to be the one in force.
 *
 * Picking a version is a statement about *when*, not just which: the map answers "what was
 * served at this instant", so a pick the marker cannot reach would leave one version named in
 * the panel and a different one drawn. A marker already inside the span is left alone — it is
 * a position the super user chose, and any instant in the span selects the same version.
 */
export const instantThatSelects = (segment: ValiditySegment, current: Date): Date => {
    const isBefore = current.getTime() < segment.from.getTime();
    // The end of a span is the instant its successor took over, so it is outside this one.
    const isAfter = segment.to !== null && current.getTime() >= segment.to.getTime();

    return isBefore || isAfter ? segment.from : current;
};

/**
 * The span the marker is standing on.
 *
 * The map draws whatever the engine serves at the marker's instant, and this is that same
 * version read off the timeline instead of asked for again: the spans already encode validity,
 * so the answer is local, immediate, and cannot disagree with the bar the marker sits over.
 *
 * A boundary belongs to the version taking over — "in force until X" is not in force *at* X —
 * which is how the engine resolves it too.
 */
export const segmentInForceAt = (segments: ValiditySegment[], at: Date): ValiditySegment | undefined =>
    segments.find(segment => at.getTime() >= segment.from.getTime() && (segment.to === null || at.getTime() < segment.to.getTime()));

/** The version of that span. */
export const versionInForceAt = (segments: ValiditySegment[], at: Date): StacItem | undefined => segmentInForceAt(segments, at)?.version;
